import React, { useEffect, useReducer } from "react";
import { Box, Alert, AlertTitle, Tooltip, IconButton, Switch, FormControlLabel, TextField, Button } from "@mui/material";
import { getSession } from "../../../auth.js";
import api from "../../../api";
import Loading from "../../Loading/index.js";
import LoadingButton from "@mui/lab/LoadingButton";
import AddScoutForm from "./AddScoutForm/index.js";
import ConnectModal from "./ConnectModal/index.js";
import useNotification from "../../Notification";

import DeleteIcon from '@mui/icons-material/Delete';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import ConfirmationDialogCaptcha from '../../Dialogs/ConfimationDialogCaptcha'
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  gridClasses,
  ptBR
} from '@mui/x-data-grid-pro';
import { ptBR as corePtBR } from '@mui/material/locale';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider
} from '@mui/material/styles';
import defaultReducer from "../../../utils/defaultReducer.js";
import HistoryIcon from '@mui/icons-material/History';
import VoiceOverOffRoundedIcon from '@mui/icons-material/VoiceOverOffRounded';
import History from "./History/index.js";
import IgnoreList from "./IgnoreList/index.js";
import EmailIcon from '@mui/icons-material/Email';
import { validateEmail } from '../../Report/NewDetailedRegister/report_export_data'
import { EmailTextField } from "./EmailDialogs.js/index.js";

import ContentUnavailable from "../../ContentUnavailable"
import { useMenu } from "../../../contexts/MenuContext"
import ScoutLogo from "../../../assets/images/kwik-scout-logo.png"

const SCOUT_BEST_PRACTICES_GUIDE_URL = "https://docs.google.com/document/d/e/2PACX-1vQ-Y8-xkmJ-naghEvlius-ywMm32djXShYsDMFTGrOIOVd5vETmWz8VFLN14c5zJSGeCWvjOBix_LLS/pub"

let variable_enterprise = null

const status_dict = {
  'open': (<span style={{ color: 'green' }}>Conectado</span>),
  'connecting': (<span style={{ color: 'orange' }}>Conectando</span>),
  'close': (<span style={{ color: 'red' }}>Desconectado</span>)
}

const theme = createTheme({
  palette: {
    primary: { main: '#21446C' },
  },
}, ptBR, corePtBR);


const defColumns = (state, setState) => {
  const columns = []
  columns.push({ field: 'name', headerName: 'Nome', flex: 1 })
  columns.push({ field: 'phone_number', headerName: 'Telefone', flex: 1 })
  columns.push({
    field: 'status', headerName: 'Status', flex: 1
    , renderCell: ({ value, row }) =>
      (row.connection_state === 'export' ? <span style={{ color: 'orange' }}>Exportando</span> : status_dict[value || row.connection_state])
  })
  columns.push({ field: 'usage', headerName: 'Conversas', flex: 1 })
  columns.push({
    field: 'actions', headerName: 'Ações', flex: 1,
    renderCell: ({ row }) => (
      <>
        {row.connection_state !== 'export' &&
          <>
            <Tooltip title={row.status == "open" ? "Desconectar" : "Conectar"}>
              <IconButton className={'action-button'} sx={{ color: '#21446C' }} onClick={() => {
                if (row.status === 'open') {
                  setState({ confirmDisconnect: row })
                } else {
                  connectDisconnect(row.id, row.status, row.phone_number, setState)
                }

              }}>
                <MultipleStopIcon className='editicon' />
              </IconButton>
            </Tooltip>
            {row.status !== 'open' && <Tooltip title="Reenviar convite">
              <IconButton className={'action-button'} sx={{ color: '#21446C' }} onClick={() => setState({ showRenotify: row })}>
                <EmailIcon />
              </IconButton>
            </Tooltip>}
            <Tooltip title="Histórico de conexões">
              <IconButton className={'action-button'} sx={{ color: '#21446C' }} onClick={() => setState({ showHistory: row })}>
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            {variable_enterprise.show_scout_contact_exclude &&
              <Tooltip title="Lista de Exclusão">
                <IconButton className={'action-button'} sx={{ color: '#21446C' }} onClick={() => setState({ showIgnoreList: row })}>
                  <VoiceOverOffRoundedIcon />
                </IconButton>
              </Tooltip>}
            {(row.status !== 'open') &&
              <Tooltip title="Excluir">
                <IconButton className={'action-button delete'} onClick={() => row.status === 'open' ? useNotification(
                  "Erro!",
                  `É necessário desconectar o número antes de excluí-lo.`,
                  "danger"
                ) : setState({ confirmDelete: row })}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>}
          </>
        }
      </>
    )
  })
  return columns
}

const getAccounts = async (setState) => {
  setState({ loading: true })

  const params = new URLSearchParams({
    fullFetch: 1,
  });

  const [accounts, users] = await Promise.all([
    api.get(`api/enterprises/${variable_enterprise.id}/scout_accounts/?${params.toString()}`,{ timeout: 30000 }),
    api.get(`api/enterprises/${variable_enterprise.id}/users/get_users_emails/`,{ timeout: 30000 })
  ])
  
  if (accounts.status === 200 && users.status === 200) {
    setState({ loading: false, users: users.data, accounts: accounts.data, showFormModal: false })
  } else {
    useNotification(
      "Erro!",
      `Falha ao consultar contas.`,
      "danger"
    );
    setState({ loading: false })
  }
}

const addScout = async (name, number, email, createdBy, setState) => {
  setState({ loading: true })
  const res = await api.post(
    `api/enterprises/${variable_enterprise.id}/scout_accounts/`,
    {
      name: name.trim(),
      phone_number: number,
      email: email,
      created_by_id: createdBy
    }
  );
  if (res.status === 409) {
    useNotification(
      "Erro!",
      `Este número de telefone já está em uso por outra conta.`,
      "danger"
    );
    setState({ loading: false })
    return
  }

  if (res.status === 400) {
    useNotification(
      "Erro!",
      `Número máximo de licenças atingido.`,
      "danger"
    );
    setState({ loading: false })
    return
  }

  getAccounts(setState);
};

const deleteScout = async (id, setState) => {
  setState({ loading: true })
  const res = await api.delete(
    `api/enterprises/${variable_enterprise.id}/scout_accounts/${id}/`
  );
  if (res.status !== 204) {
    useNotification(
      "Erro!",
      `[${res.status}] ${res.data}`,
      "danger"
    );
  }
  getAccounts(setState);
};

const connectDisconnect = async (id, status, phone_number, setState) => {
  if (status == "open") {
    setState({ loading: true })
    const res = await api.post(
      `api/enterprises/${variable_enterprise.id}/scout_accounts/${id}/disconnect/`
    );

    if (res.status === 200) {
      getAccounts(setState);
    } else {
      useNotification(
        "Erro!",
        `[${res.status}] Falha na desconexão do número ${phone_number}.`,
        "danger"
      );
    }
    setState({ loading: false })

  } else {
    setState({ showConnectModal: { id, phone_number } });
  }
};

const renotify = async (data, setState) => {
  setState({ loading: true })
  const res = await api.get(
    `api/enterprises/${variable_enterprise.id}/scout_accounts/${data.id}/renotify/`
  );
  setState({ loading: false })
  if (res.status === 200) {
    useNotification(
      "Sucesso!",
      `Notificação reenviada com sucesso.`,
      "success"
    );
  } else {
    useNotification(
      "Erro!",
      `Não foi possível reenviar a notificação`,
      "danger"
    );
  }
}

const exportallmessages = async (data, handleClose) => {
  const enterprise = getSession().profile.enterprise;

  const res = await api.post(`api/enterprises/${enterprise.id}/scout_accounts/${data.id}/exportallmessages/`, {
    ...data,
  })
  if (res.status === 200) {
    useNotification(
      'Sucesso!',
      'A exportação de conversas foi iniciada e em breve estará disponível na caixa de e-mail selecionada. O número será excluido automáticamente.',
      'success'
    );

    handleClose()
  }
  else if (res.status === 409) {
    useNotification(
      'Recurso ocupado',
      'Já existe uma exportação em andamento para sua empresa, aguarde até que ela seja concluída, ou tente novamente em 1 hora.',
      'danger'
    );
  }
  else {
    useNotification(
      'Não encontrado',
      'Não foi possível exportar as conversas desse número.',
      'danger'
    );
  }
}

const CustomToolbar = (setState) => {

  return (
    <GridToolbarContainer className={gridClasses.toolbarContainer}>
      <LoadingButton
        size="small"
        component="label"
        startIcon={<AddCircleIcon />}
        onClick={() => { setState({ showFormModal: true }) }}
      >
        Adicionar
      </LoadingButton>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const SettingsPage = () => {
  const { configurableUrls } = useMenu()
  const enterprise = getSession().profile.enterprise;
  variable_enterprise = enterprise
  const [state, setState] = useReducer(defaultReducer, {
    accounts: [],
    users: [],
    showFormModal: false,
    showConnectModal: null,
    loading: false,
    confirmDelete: null,
    editComponent: null,
    confirmDisconnect: null,
    showHistory: null,
    showRenotify: null,
    showIgnoreList: null,
    exportAll: false,
    email: '',
    type: 'pdf' || 'csv'
  })

  useEffect(() => {
    getAccounts(setState);
  }, []);

  const validatedEmail = validateEmail(state.email)

  if (enterprise.platforms.scout) {
    return (
      <Box className="input-settings settings-body">
        <Box className="settings-flexcolumn">
          <Loading loading={state.loading} />
          {state.showRenotify &&
            <ConfirmationDialogCaptcha open={true}
              title="Confirmação"
              description={
                <span>
                  Deseja mesmo reenviar o convite para "{state.showRenotify.name}" no e-mail "{state.showRenotify.email}", digite: <span style={{ 'fontWeight': 'bold' }}>reenviar</span> no campo abaixo.</span>}
              handleClose={() => { setState({ showRenotify: null }); }}
              handleSubmit={() => { setState({ showRenotify: null }); renotify(state.showRenotify, setState); }}
              captcha={'reenviar'}
              alternateNo="Cancelar"
              alternateYes="Confirmar"
              alternateName=""
            />
          }
          {state.confirmDisconnect &&
            <ConfirmationDialogCaptcha open={true}
              title="Confirmação"
              description={
                <span>
                  Deseja mesmo desconectar o número {state.confirmDisconnect.phone_number} ({state.confirmDisconnect.name}), digite: <span style={{ 'fontWeight': 'bold' }}>desconectar</span> no campo abaixo.</span>}
              handleClose={() => { setState({ confirmDisconnect: null }); }}
              handleSubmit={() => { setState({ confirmDisconnect: null }); connectDisconnect(state.confirmDisconnect.id, state.confirmDisconnect.status, state.confirmDisconnect.phone_number, setState); }}
              captcha={'desconectar'}
              alternateNo="Cancelar"
              alternateYes="Confirmar"
              alternateName=""
            />
          }
          {state.confirmDelete &&
            <ConfirmationDialogCaptcha open={true}
              title="Confirmação"
              description={
                <>
                  <span>Ao concordar em excluir o número {state.confirmDelete.phone_number} ({state.confirmDelete.name}), <span style={{ fontWeight: 'bold' }}>todas as conversas com esse número serão apagadas do Kwik Scout e não poderão mais ser recuperadas.</span> </span>
                  <br></br>
                  <br></br>
                  <span>

                    <FormControlLabel
                      sx={{ margin: 0 }}
                      control={
                        <Switch
                          onClick={() => setState({ exportAll: !state.exportAll })}
                          checked={state.exportAll}
                        />
                      }
                      label=""
                    />
                    Marque aqui para baixar um arquivo com todas as conversas antes de remover este usuário.</span>
                  {state.exportAll && <>
                    <EmailTextField loadEmail={(email) => setState({ email: email })} />
                    <Box className='BuilderBotSelectorBox'>
                      <Button
                        variant={state.type === 'pdf' ? 'contained' : ''}
                        onClick={() => setState({ type: 'pdf' })}
                      >
                        PDF
                      </Button>
                      <Button
                        variant={state.type === 'csv' ? 'contained' : ''}
                        onClick={() => setState({ type: 'csv' })}
                      >
                        CSV
                      </Button>
                    </Box>
                  </>
                  }
                  <br></br>
                  <span>Digite: <span style={{ 'fontWeight': 'bold' }}>remover</span> no campo abaixo para confirmar.</span>
                </>}
              handleClose={() => { setState({ confirmDelete: null }); }}
              handleSubmit={() => {
                if (state.exportAll) {
                  exportallmessages({ email: state.email, id: state.confirmDelete.id, type: state.type }, async () => {
                    await getAccounts(setState)
                    setState({ exportAll: null, confirmDelete: null })
                  })
                } else {
                  setState({ confirmDelete: null });
                  deleteScout(state.confirmDelete.id, setState);
                }
              }}
              disabled={!validatedEmail && state.exportAll}
              captcha={'remover'}
              alternateNo="Cancelar"
              alternateYes="Confirmar"
              alternateName=""
            />
          }
          {state.showHistory && (
            <History
              data={state.showHistory}
              handleClose={() => setState({ showHistory: null })}
            />
          )}
          {state.showIgnoreList && (
            <IgnoreList
              data={state.showIgnoreList}
              handleClose={() => setState({ showIgnoreList: null })}
              setLoading={(e) => setState({ loading: e })}
            />
          )}
          {state.showFormModal && (
            <AddScoutForm
              open={true}
              users={state.users}
              handleClose={() => setState({ showFormModal: false })}
              submit={(...args) => addScout(...args, setState)}
            />
          )}
          {state.showConnectModal !== null && (
            <ConnectModal
              account={state.showConnectModal}
              enterpriseId={enterprise.id}
              handleClose={() => {
                getAccounts(setState);
                setState({ showConnectModal: null })
              }}
            />
          )}
          <Alert className="telegram-info" severity="info">
            <AlertTitle>
              Para saber como conectar um Scout <a href="https://instant.getoutline.com/s/kwik-integracoes/doc/scout-Bmj6w7Jccq" target="_blank" rel="noopener noreferrer">clique aqui.</a>
            </AlertTitle>
            <Box className="push-s-top">
              Clique no botão Adicionar
            </Box>
          </Alert>
          <Alert className="telegram-info" severity="info">
            <AlertTitle>
              Guia de boas práticas do Kwik Scout <a href={SCOUT_BEST_PRACTICES_GUIDE_URL} target="_blank" rel="noopener noreferrer">clique aqui.</a>
            </AlertTitle>
          </Alert>
          <Box className="category-title">
            Telefones: {state.accounts.length} de {enterprise.platforms.scout_licenses} licenças utilizadas.
          </Box>
          <Box className="settings-body-bot">
            <StyledEngineProvider injectFirst >
              <ThemeProvider theme={theme}>
                <DataGridPro
                  pageSize={100}
                  pagination
                  disableSelectionOnClick
                  disableDensitySelector
                  density="compact"
                  columns={defColumns(state, setState)}
                  rows={state.accounts}
                  components={{
                    Toolbar: () => CustomToolbar(setState),
                  }}
                />
              </ThemeProvider>
            </StyledEngineProvider>
          </Box>
        </Box>

      </Box >
    );
  } else {
    return (
      <ContentUnavailable 
        icon={<img src={ScoutLogo} alt="Kwik Scout" style={{ width: 350 }} />}
        title="Recurso indisponível para sua conta."
        description={
          <a href={configurableUrls?.scout ? configurableUrls.scout : "https://scout.kwik.app.br/"} target="_blank" rel="noopener noreferrer">
                Para mais informações sobre o Kwik Scout, clique aqui.
            </a>
        }
      />
    );
  }
};

export default SettingsPage;
